import React from "react";
import { List, Card, Checkbox, Typography } from "antd";
import { DndContext, closestCenter } from "@dnd-kit/core";
import { HolderOutlined } from "@ant-design/icons";
import {
  arrayMove,
  SortableContext,
  rectSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

const { Text } = Typography;

const SortableItem = ({ id, index, onRemove, editMode }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id, disabled: !editMode });

  const style = {
    transform: CSS.Translate.toString(transform),
    transition,
    zIndex: isDragging ? 100 : 1,
    touchAction: "none",
    cursor: !editMode ? "default" : isDragging ? "grabbing" : "grab",
  };

  return (
    <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
      <List.Item style={{ padding: 0 }}>
        <Card
          size="small"
          hoverable={editMode}
          style={{ margin: "4px", borderRadius: "4px", cursor: "inherit" }}
          bodyStyle={{ padding: "8px 12px" }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            {/* Visual Drag Handle (Dots) */}
            {editMode && (
              <HolderOutlined style={{ color: "#bfbfbf", cursor: "inherit" }} />
            )}

            <div onPointerDown={(e) => e.stopPropagation()}>
              <Checkbox
                checked
                onChange={() => onRemove(id)}
                disabled={!editMode}
                style={{ cursor: editMode ? "pointer" : "default" }}
              />
            </div>

            <Text ellipsis style={{ flex: 1 }}>
              {id}
            </Text>

            <Text type="secondary" style={{ fontSize: "12px" }}>
              ({index + 1})
            </Text>
          </div>
        </Card>
      </List.Item>
    </div>
  );
};

const DraggableGrid = ({ items = [], onReorder, onRemove, editMode }) => {
  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (active.id !== over?.id) {
      const oldIndex = items.indexOf(active.id);
      const newIndex = items.indexOf(over.id);
      onReorder(arrayMove(items, oldIndex, newIndex));
    }
  };

  return (
    <DndContext collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
      <SortableContext items={items} strategy={rectSortingStrategy}>
        <List
          grid={{ gutter: 8, xs: 1, sm: 2, md: 3, lg: 3, xl: 4 }}
          dataSource={items}
          style={{ marginBottom: "20px" }}
          renderItem={(item, index) => (
            <SortableItem
              key={item}
              id={item}
              index={index}
              onRemove={onRemove}
              editMode={editMode}
            />
          )}
        />
      </SortableContext>
    </DndContext>
  );
};

export default DraggableGrid;
